/**
 * User Edit/Create Dialog Component
 * Handles both editing existing users and creating new users
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import EmailIcon from '@mui/icons-material/Email';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EngineeringIcon from '@mui/icons-material/Engineering';
import PersonIcon from '@mui/icons-material/Person';
import PublicIcon from '@mui/icons-material/Public';

const GROUP_ICONS = {
  'ADMIN': { icon: AdminPanelSettingsIcon, color: 'error', label: 'Administrator' },
  'MODERATOR': { icon: SupervisorAccountIcon, color: 'warning', label: 'Moderator' },
  'OPERATOR': { icon: EngineeringIcon, color: 'info', label: 'Operator' },
  'STANDARD': { icon: PersonIcon, color: 'default', label: 'Standard' },
  'GUEST': { icon: PublicIcon, color: 'disabled', label: 'Guest' },
};

const READ_ONLY_ATTRS = ['sub', 'identities', 'cognito:username', 'username', 'userId'];
const TIMESTAMP_ATTRS = ['created', 'modified', 'userCreateDate', 'userLastModifiedDate', 'createdAt', 'updatedAt'];

export default function UserEditDialog({
  open,
  mode, // 'edit' | 'create'
  user, // User object for edit mode
  groups, // Available groups array
  onSave,
  onCancel,
  onForceLogout,
  onResendAccountId,
  saving,
  error
}) {
  const [formData, setFormData] = useState({
    email: '',
    handle: '',
    password: '',
    enabled: true,
    groups: [],
    attributes: {}
  });
  const [forceLoggingOut, setForceLoggingOut] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(null);

  // Initialize form data when dialog opens or mode changes
  useEffect(() => {
    if (!open) {
      // Dialog closed - reset everything
      setFormData({
        email: '',
        handle: '',
        password: '',
        enabled: true,
        groups: [],
        attributes: {}
      });
      return;
    }

    // Dialog is open - initialize based on mode
    if (mode === 'create') {
      // Create mode: always start with empty form
      setFormData({
        email: '',
        handle: '',
        password: '',
        enabled: true,
        groups: ['STANDARD'],
        attributes: {}
      });
    } else if (mode === 'edit' && user) {
      // Edit mode: populate from user
      const attrs = {};
      Object.entries(user).forEach(([key, value]) => {
        if (!['username', 'userId', 'email', 'handle', 'groups', 'enabled', 'userStatus', 'userCreateDate', 'userLastModifiedDate'].includes(key)) {
          attrs[key] = value ?? '';
        }
      });

      setFormData({
        email: user.email || '',
        handle: user.handle || '',
        password: '',
        enabled: user.enabled ?? true,
        groups: user.groups || [],
        attributes: attrs
      });
    }
  }, [open, mode, user]);

  const handleGroupSelect = (groupName) => {
    setFormData(prev => ({
      ...prev,
      groups: [groupName] // Radio selection - only one group
    }));
  };

  const handleAttributeChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value }
    }));
  };

  const handleSave = () => {
    onSave({
      ...formData,
      username: user?.username // Include username for edit mode
    });
  };

  const handleForceLogout = async () => {
    if (!user?.username || !onForceLogout) return;

    setForceLoggingOut(true);
    try {
      await onForceLogout(user.username);
    } finally {
      setForceLoggingOut(false);
    }
  };

  const handleResendAccountId = async () => {
    if (!user?.username || !onResendAccountId) return;

    setResendingEmail(true);
    setResendSuccess(null);
    try {
      const result = await onResendAccountId(user.username);
      setResendSuccess(result.emailSent ? 'success' : 'warning');
      setTimeout(() => setResendSuccess(null), 5000); // Clear after 5 seconds
    } catch (error) {
      setResendSuccess('error');
      setTimeout(() => setResendSuccess(null), 5000);
    } finally {
      setResendingEmail(false);
    }
  };

  const isCreateMode = mode === 'create';
  const title = isCreateMode
    ? 'Create New User'
    : `Edit Account: ${user?.username}${user?.handle ? ` (${user.handle})` : ''}`;

  const canSave = isCreateMode
    ? formData.email && formData.handle && formData.password && formData.groups.length > 0
    : true;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Email */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            disabled={saving}
            required
          />

          {/* Handle */}
          <TextField
            fullWidth
            label="Handle"
            value={formData.handle}
            onChange={(e) => setFormData(prev => ({ ...prev, handle: e.target.value }))}
            disabled={saving}
            required={isCreateMode}
            placeholder="@user handle"
            autoComplete="off"
          />

          {/* Password (create mode only) */}
          {isCreateMode && (
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              disabled={saving}
              required
              autoComplete="new-password"
              helperText="At least 8 characters with uppercase, lowercase, and number"
            />
          )}

          {/* Account Enabled (edit mode only) */}
          {!isCreateMode && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                  disabled={saving}
                />
              }
              label={formData.enabled ? 'Account Enabled' : 'Account Disabled'}
            />
          )}

          {/* Groups - Radio selection */}
          <Divider>Group</Divider>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            {groups.map(groupName => {
              const config = GROUP_ICONS[groupName] || { icon: PersonIcon, color: 'default', label: groupName };
              const Icon = config.icon;
              const isSelected = formData.groups.includes(groupName);

              return (
                <Tooltip key={groupName} title={config.label || groupName}>
                  <IconButton
                    onClick={() => handleGroupSelect(groupName)}
                    disabled={saving}
                    color={isSelected ? config.color : 'default'}
                    sx={{
                      width: 56,
                      height: 56,
                      border: 2,
                      borderColor: isSelected ? `${config.color}.main` : 'divider',
                      bgcolor: isSelected ? `${config.color}.lighter` : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? `${config.color}.light` : 'action.hover'
                      }
                    }}
                  >
                    <Icon fontSize="large" />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>

          {/* Additional Attributes (edit mode only) */}
          {!isCreateMode && Object.keys(formData.attributes).length > 0 && (
            <>
              <Divider>Attributes</Divider>
              {Object.entries(formData.attributes)
                .filter(([key]) => !TIMESTAMP_ATTRS.includes(key)) // Filter out timestamps
                .map(([key, value]) => (
                  <TextField
                    key={key}
                    label={key}
                    value={value}
                    size="small"
                    fullWidth
                    disabled={READ_ONLY_ATTRS.includes(key) || saving}
                    helperText={READ_ONLY_ATTRS.includes(key) ? 'Read-only' : undefined}
                    onChange={(e) => handleAttributeChange(key, e.target.value)}
                  />
                ))}

              {/* Timestamps - Low visual priority */}
              {Object.entries(formData.attributes)
                .filter(([key]) => TIMESTAMP_ATTRS.includes(key))
                .map(([key, value]) => (
                  <TextField
                    key={key}
                    label={key}
                    value={value}
                    size="small"
                    fullWidth
                    disabled
                    InputProps={{
                      sx: {
                        fontSize: '0.75rem',
                        color: 'text.disabled'
                      }
                    }}
                    InputLabelProps={{
                      sx: { fontSize: '0.75rem' }
                    }}
                  />
                ))}
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {resendSuccess === 'success' && <Alert severity="success">Account ID email sent successfully</Alert>}
          {resendSuccess === 'warning' && <Alert severity="warning">Email delivery failed. Please try again.</Alert>}
          {resendSuccess === 'error' && <Alert severity="error">Failed to send email. Please try again.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isCreateMode && (
          <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
            {onResendAccountId && (
              <Button
                onClick={handleResendAccountId}
                color="info"
                startIcon={<EmailIcon />}
                disabled={saving || forceLoggingOut || resendingEmail}
              >
                {resendingEmail ? 'Sending...' : 'Resend Account ID'}
              </Button>
            )}
            {onForceLogout && (
              <Button
                onClick={handleForceLogout}
                color="warning"
                startIcon={<LogoutIcon />}
                disabled={saving || forceLoggingOut || resendingEmail}
              >
                {forceLoggingOut ? 'Logging out...' : 'Force Logout'}
              </Button>
            )}
          </Box>
        )}
        <Button onClick={onCancel} disabled={saving || forceLoggingOut || resendingEmail}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || forceLoggingOut || resendingEmail || !canSave}>
          {saving ? <CircularProgress size={20} /> : (isCreateMode ? 'Create User' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
